/**
 * Spin wheel — prize wheel for the Oppa Lab giveaway page.
 *
 * Two modes, chosen by whether the section has an API URL configured:
 *
 *   SERVER MODE (api_url set) — the Cloudflare Worker owns the draw, the weights and the
 *   discount codes. This file only animates a result the server already decided, so the
 *   page source contains no codes and the outcome cannot be forged from devtools.
 *   Flow: POST /spin -> {token, slug}  then  POST /claim -> {code}.
 *
 *   LOCAL MODE (api_url blank) — the original client-side draw. Kept so the page still
 *   works if the API is unreachable or unconfigured, but it necessarily ships the codes
 *   and weights to the browser, so it is not the configuration to run a real campaign on.
 *
 * `?wheel_test=1` lifts the one-spin-per-person limit and skips persistence.
 */

const STORAGE_PREFIX = 'oppa-wheel:';
const FULL_SPINS = 8;
const SPIN_MS = 5200;

class SpinWheel extends HTMLElement {
  /**
   * The parser upgrades a custom element as soon as it sees the opening tag, which is
   * BEFORE the children exist. Everything below reads child nodes, so initialisation has
   * to wait until they are parsed or the wheel silently renders nothing.
   */
  connectedCallback() {
    if (this.querySelector('[data-wheel-config]')) {
      this.init();
    } else if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
    } else {
      requestAnimationFrame(() => this.init());
    }
  }

  init() {
    if (this.initialised) return;

    const configEl = this.querySelector('[data-wheel-config]');
    if (!configEl) {
      console.error('[spin-wheel] no config found');
      return;
    }
    this.initialised = true;

    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (error) {
      console.error('[spin-wheel] could not parse config', error);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    this.submitted = params.get('contact_posted') === 'true';
    this.testMode = params.get('wheel_test') === '1';

    this.campaign = this.config.campaign || 'default';
    this.api = String(this.config.api_url || '').replace(/\/+$/, '');
    this.storageKey = STORAGE_PREFIX + this.campaign;

    this.svg = this.querySelector('[data-wheel-svg]');
    this.button = this.querySelector('[data-wheel-spin]');
    this.resultEl = this.querySelector('[data-wheel-result]');
    this.formEl = this.querySelector('[data-wheel-form]');
    this.apiForm = this.querySelector('[data-wheel-api-form]');
    this.errorEl = this.querySelector('[data-wheel-error]');
    this.successEl = this.querySelector('[data-wheel-success]');
    this.prizeInput = this.querySelector('[data-wheel-prize-input]');
    this.codeEl = this.querySelector('[data-wheel-code]');
    this.codeValueEl = this.querySelector('[data-wheel-code-value]');
    this.oddsEl = this.querySelector('[data-wheel-odds]') || document.querySelector('[data-wheel-odds]');
    this.spinning = false;

    if (this.testMode) {
      const badge = document.createElement('p');
      badge.className = 'spin-wheel__testbadge';
      // In server mode "nothing is saved" would be a lie: only the browser-side
      // one-spin limit is lifted. Every spin still hits the Worker, is recorded, and
      // reserves stock.
      badge.textContent = this.api
        ? 'MODO TESTE — giros ilimitados no navegador (cada giro conta no servidor)'
        : 'MODO TESTE — giros ilimitados, nada é salvo';
      this.prepend(badge);
    }

    if (this.api) this.startServerMode();
    else this.startLocalMode();
  }

  /* ------------------------------------------------------------- server mode */

  async startServerMode() {
    // Labels/colours come from the API too, so the wheel always reflects what the server
    // will actually draw from. The Liquid-rendered wedges are only a fallback for when
    // the API can't be reached.
    try {
      const res = await fetch(`${this.api}/wheel?campaign=${encodeURIComponent(this.campaign)}`);
      const data = await res.json();
      if (!Array.isArray(data.wedges) || !data.wedges.length) throw new Error('empty wheel');
      this.wedges = data.wedges.map((w) => ({ ...w, type: w.kind }));
      this.renderOdds(data.wedges);
    } catch (error) {
      console.warn('[spin-wheel] falling back to embedded wedges', error);
      this.wedges = this.config.wedges || [];
    }

    if (!this.wedges.length) return;
    this.render();

    let previous = this.testMode ? null : this.readStoredResult();

    // A result saved by the old client-side build — or by a previous campaign — carries
    // no claim token, so its prize can never be redeemed. Restoring it would strand the
    // visitor: the spin button hides, and every claim fails with "spin first". Discard
    // it and let them spin for real instead.
    if (previous && !previous.token) {
      this.clearStored();
      previous = null;
    }

    if (previous) this.restore(previous);
    else this.button?.addEventListener('click', () => this.serverSpin());

    this.apiForm?.addEventListener('submit', (event) => this.submitClaim(event));
  }

  async serverSpin() {
    if (this.spinning) return;
    this.spinning = true;
    this.button.disabled = true;
    this.setAttribute('data-state', 'spinning');

    let data;
    try {
      const res = await fetch(`${this.api}/spin`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ campaign: this.campaign }),
      });
      data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'spin_failed');
    } catch (error) {
      this.spinning = false;
      this.button.disabled = false;
      this.setAttribute('data-state', 'idle');
      this.showError(
        String(error.message) === 'rate_limited'
          ? 'Você já girou várias vezes. Tente de novo mais tarde.'
          : 'Não foi possível girar agora. Tente de novo em instantes.'
      );
      return;
    }

    const index = Math.max(0, this.wedges.findIndex((w) => w.slug === data.slug));
    const wedge = { ...this.wedges[index], ...data };

    await this.animateTo(index);
    this.spinning = false;
    this.token = data.token;

    if (!this.testMode) this.storeResult(index, this.rotation, data.token);
    this.reveal(wedge);
    this.setAttribute('data-state', 'done');

    if (this.testMode && this.button) {
      this.button.disabled = false;
      this.button.hidden = false;
    }
  }

  async submitClaim(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const submit = form.querySelector('[type="submit"]');
    const value = (id) => form.querySelector(`#${id}`)?.value?.trim() || '';

    const email = value('WheelEmail');
    // Consent is expressed either by an explicit checkbox (giveaway page) or by
    // submitting the form under a disclosed notice (in-article embed). Only enforce
    // the checkbox where one is actually rendered.
    const consentBox = form.querySelector('#WheelConsent');
    if (!email || (consentBox && !consentBox.checked)) {
      this.showError('Preencha o e-mail para liberar seu prêmio.');
      return;
    }
    if (!this.token) {
      this.showError('Gire a roleta antes de resgatar.');
      return;
    }

    this.hideError();
    if (submit) submit.disabled = true;

    try {
      const res = await fetch(`${this.api}/claim`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          token: this.token,
          email,
          name: value('WheelName'),
          tiktok: value('WheelTiktok'),
          instagram: value('WheelInstagram'),
          answer: value('WheelAnswer'),
          // Which article this wheel was embedded in. Rendered server-side from
          // article.handle, so it is authoritative rather than parsed from the URL.
          source_handle: this.config.source_handle || '',
          source_path: window.location.pathname,
          referrer: document.referrer || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'claim_failed');

      form.hidden = true;
      if (this.successEl) this.successEl.hidden = false;

      // The server is the authority on which prize this email actually gets. Someone who
      // already entered gets their ORIGINAL prize back, not the one the wheel just landed
      // on — so the headline has to be rewritten from the response, or the page shows
      // "you won 10% OFF" above a GIRA15 coupon.
      const titleEl = this.resultEl?.querySelector('[data-wheel-result-title]');
      const bodyEl = this.resultEl?.querySelector('[data-wheel-result-body]');
      if (titleEl) {
        if (data.result_title) titleEl.textContent = data.result_title;
        else if (data.repeat && data.label) titleEl.textContent = 'Seu prêmio: ' + data.label;
      }
      if (bodyEl) {
        if (data.already_entered) {
          bodyEl.textContent =
            'Você já tinha participado com esse e-mail, então vale o prêmio da primeira vez.';
        } else if (data.result_text) {
          bodyEl.textContent = data.result_text;
        }
      }

      if (data.code && this.codeEl) {
        this.codeEl.hidden = false;
        if (this.codeValueEl) this.codeValueEl.textContent = data.code;
      }
      if (!this.testMode) this.storeClaim(data.code);
      (this.codeEl || this.successEl)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (error) {
      if (submit) submit.disabled = false;
      const message = String(error.message);

      // A token the server no longer honours is a dead end unless we hand the spin back.
      if (message === 'expired' || message === 'unknown_token') {
        this.resetForRespin('Esse giro expirou. Gire a roleta de novo.');
        return;
      }
      this.showError('Não foi possível resgatar agora. Tente de novo em instantes.');
    }
  }

  /** Return the wheel to a spinnable state after an unusable token. */
  resetForRespin(message) {
    this.clearStored();
    this.token = null;
    if (this.resultEl) this.resultEl.hidden = true;
    if (this.formEl) this.formEl.hidden = true;
    if (this.button) {
      this.button.hidden = false;
      this.button.disabled = false;
    }
    this.setAttribute('data-state', 'idle');
    this.showError(message);
    if (this.errorEl) this.prepend(this.errorEl);
  }

  renderOdds(wedges) {
    if (!this.oddsEl) return;
    this.oddsEl.innerHTML = wedges
      .map(
        (w) =>
          `<tr><td>${escapeHtml(w.label)}</td><td>${Number(w.chance).toFixed(2)}%</td></tr>`
      )
      .join('');
  }

  /* -------------------------------------------------------------- local mode */

  startLocalMode() {
    this.wedges = this.config.wedges || [];
    if (!this.wedges.length) return;
    this.render();

    const previous = this.testMode ? null : this.readStoredResult();
    if (previous) {
      this.restore(previous);
    } else {
      this.button?.addEventListener('click', () => this.localSpin());
    }
  }

  async localSpin() {
    if (this.spinning || (!this.testMode && this.readStoredResult())) return;
    this.spinning = true;
    this.button.disabled = true;
    this.setAttribute('data-state', 'spinning');

    const index = this.pick();
    await this.animateTo(index);

    this.spinning = false;
    if (!this.testMode) this.storeResult(index, this.rotation, null);
    this.reveal(this.wedges[index]);
    this.setAttribute('data-state', 'done');

    if (this.testMode && this.button) {
      this.button.disabled = false;
      this.button.hidden = false;
    }
  }

  /** Local-mode weighted pick. Server mode never calls this. */
  pick() {
    const eligible = this.wedges
      .map((wedge, index) => ({ wedge, index }))
      .filter(({ wedge }) => {
        if (!(wedge.weight > 0)) return false;
        if (wedge.type === 'shirt' && wedge.stock !== null && wedge.stock <= 0) return false;
        return true;
      });

    if (!eligible.length) {
      const fallback = this.wedges.findIndex((wedge) => wedge.type === 'coupon');
      return fallback >= 0 ? fallback : 0;
    }

    const total = eligible.reduce((sum, { wedge }) => sum + wedge.weight, 0);
    let ticket = Math.random() * total;
    for (const { wedge, index } of eligible) {
      ticket -= wedge.weight;
      if (ticket <= 0) return index;
    }
    return eligible[eligible.length - 1].index;
  }

  /* ---------------------------------------------------------------- drawing */

  render() {
    const size = 100;
    const center = size / 2;
    const radius = center - 1;
    const step = 360 / this.wedges.length;
    const parts = [];

    this.wedges.forEach((wedge, index) => {
      const start = index * step;
      const end = start + step;
      parts.push(
        `<path d="${this.arcPath(center, radius, start, end)}" fill="${escapeAttr(wedge.color)}" stroke="rgba(255,255,255,.85)" stroke-width=".4"/>`
      );

      // Labels run RADIALLY, along the wedge bisector — not tangentially across it.
      // A wedge is narrow at the hub and wide at the rim, so tangential text only has
      // 2*r*sin(step/2) of room (~20 units here) and anything longer crosses into the
      // neighbouring wedge. Running the text outward along the bisector keeps it inside
      // its own wedge by construction and roughly doubles the usable length.
      //
      // Rotation: mid-90 puts the baseline along the outward radius. On the left half
      // that comes out upside down, so those flip to mid+90 and read inward instead.
      const mid = start + step / 2;
      const flipped = mid > 180;
      const labelAngle = flipped ? mid + 90 : mid - 90;
      const point = polar(center, radius * 0.58, mid);
      parts.push(
        `<text x="${point.x.toFixed(2)}" y="${point.y.toFixed(2)}"
               transform="rotate(${labelAngle.toFixed(2)} ${point.x.toFixed(2)} ${point.y.toFixed(2)})"
               text-anchor="middle" dominant-baseline="middle"
               font-size="${labelSize(wedge.label, this.wedges.length)}"
               font-weight="700" fill="${escapeAttr(wedge.text_color || '#2b2b2b')}"
               style="pointer-events:none">${escapeHtml(wedge.label)}</text>`
      );
    });

    this.svg.innerHTML = `<g data-wheel-rotor style="transform-origin:${center}px ${center}px">${parts.join('')}</g>`;
    this.rotor = this.svg.querySelector('[data-wheel-rotor]');
  }

  arcPath(center, radius, startDeg, endDeg) {
    const a = polar(center, radius, startDeg);
    const b = polar(center, radius, endDeg);
    const largeArc = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${center} ${center} L ${a.x.toFixed(2)} ${a.y.toFixed(2)} A ${radius} ${radius} 0 ${largeArc} 1 ${b.x.toFixed(2)} ${b.y.toFixed(2)} Z`;
  }

  /** Spin the rotor so `index`'s bisector lands under the pointer at 12 o'clock. */
  animateTo(index) {
    const step = 360 / this.wedges.length;
    const wedgeCenter = index * step + step / 2;
    // Repeated spins (test mode) accumulate onto the current angle so the wheel always
    // turns forwards rather than snapping backwards.
    const base = Math.ceil((this.rotation || 0) / 360) * 360;
    this.rotation = base + FULL_SPINS * 360 + ((360 - wedgeCenter) % 360);

    this.rotor.style.transition = `transform ${SPIN_MS}ms cubic-bezier(.16,.84,.28,1)`;
    this.rotor.style.transform = `rotate(${this.rotation}deg)`;

    return new Promise((resolve) => window.setTimeout(resolve, SPIN_MS));
  }

  /* ------------------------------------------------------------------ result */

  reveal(wedge) {
    if (this.resultEl) {
      this.resultEl.hidden = false;
      const title = this.resultEl.querySelector('[data-wheel-result-title]');
      const body = this.resultEl.querySelector('[data-wheel-result-body]');
      if (title) title.textContent = wedge.result_title || wedge.label;
      if (body) body.textContent = wedge.result_text || '';
    }
    if (this.prizeInput) this.prizeInput.value = `${wedge.label} (${wedge.type || wedge.kind})`;
    if (this.formEl) this.formEl.hidden = false;
    if (this.button && !this.testMode) this.button.hidden = true;

    // Local mode only: Shopify reloads the page on contact-form post, so the code is
    // revealed on the way back. Server mode reveals it inline in submitClaim().
    if (!this.api && this.submitted && wedge.type !== 'shirt' && wedge.code && this.codeEl) {
      this.codeEl.hidden = false;
      if (this.codeValueEl) this.codeValueEl.textContent = wedge.code;
    }

    const focusTarget = this.submitted ? this.codeEl || this.formEl : this.formEl;
    focusTarget?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  hideError() {
    if (this.errorEl) this.errorEl.hidden = true;
  }

  /* ------------------------------------------------------------- persistence */

  restore(previous) {
    const wedge = this.wedges[previous.index] || this.wedges[0];
    if (this.rotor) {
      this.rotation = previous.rotation || 0;
      this.rotor.style.transform = `rotate(${this.rotation}deg)`;
    }
    this.token = previous.token || null;
    this.reveal(wedge);
    this.setAttribute('data-state', 'done');

    // Already claimed on a previous visit — show the code straight away.
    if (previous.code && this.codeEl) {
      this.codeEl.hidden = false;
      if (this.codeValueEl) this.codeValueEl.textContent = previous.code;
      if (this.apiForm) this.apiForm.hidden = true;
      if (this.successEl) this.successEl.hidden = false;
    }
  }

  storeResult(index, rotation, token) {
    this.write({ index, rotation, token, at: Date.now() });
  }

  storeClaim(code) {
    const current = this.readStoredResult() || {};
    this.write({ ...current, code });
  }

  clearStored() {
    try {
      window.localStorage.removeItem(this.storageKey);
    } catch (error) {
      /* private mode — nothing was stored anyway */
    }
  }

  write(payload) {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch (error) {
      /* private mode — the spin still works, it just won't persist */
    }
  }

  readStoredResult() {
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return typeof parsed?.index === 'number' ? parsed : null;
    } catch (error) {
      return null;
    }
  }
}

/* ---------------------------------------------------------------- helpers */

function polar(center, radius, degrees) {
  // 0deg = 12 o'clock, increasing clockwise.
  const radians = ((degrees - 90) * Math.PI) / 180;
  return { x: center + radius * Math.cos(radians), y: center + radius * Math.sin(radians) };
}

/**
 * Font size in viewBox units. Long labels get scaled down until they fit the radial band
 * available on the wedge. CHAR_RATIO is the measured average glyph advance per unit of
 * font-size for this theme's bold face (checked in-browser: "10% OFF" at font-size 4
 * measures 15.4 units, i.e. 7 chars x 4 x 0.55).
 */
function labelSize(label, wedgeCount) {
  const MAX_WIDTH = 32;
  const CHAR_RATIO = 0.55;
  const base = wedgeCount > 10 ? 3.4 : wedgeCount > 7 ? 4 : 4.6;
  const fitted = MAX_WIDTH / Math.max(String(label).length, 1) / CHAR_RATIO;
  return Math.min(base, fitted).toFixed(2);
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
  });
}

const escapeAttr = escapeHtml;

if (!customElements.get('spin-wheel')) {
  customElements.define('spin-wheel', SpinWheel);
}
